import { useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import { useNavGroups } from "../../hooks/useNavGroups";
import { useAuth } from "../../contexts/AuthContext";
import { roleLabel } from "../../lib/utils";
import { NavScrollIndicator } from "./NavScrollIndicator";
import { ThemeToggle } from "./ThemeToggle";

interface NavProps {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}

/**
 * Menu unico, retratil, coral, em 4 grupos (Inicio / Canais / Administracao /
 * Monitoramento) — substitui o antigo Sidebar.tsx (rail de icones + submenu).
 * Grupos vem de useNavGroups (hooks/useNavGroups.ts), mesma fonte usada pela
 * folha "Mais" da TabBar no mobile.
 */
export function Nav({ collapsed, onToggle, onNavigate }: NavProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const visibleGroups = useNavGroups();
  const itemCount = visibleGroups.reduce((n, g) => n + g.items.length, 0);
  const { user, logout } = useAuth();

  return (
    <nav className={collapsed ? "nav nav--collapsed" : "nav"} aria-label="Navegação principal">
      <div className="cluster" style={{ marginBottom: ".5rem" }}>
        <Link to="/" onClick={onNavigate} className="nav__item grow" title="Vitrine Hiit">
          <img
            src="/brand/hiit-logo-branco.png"
            alt=""
            aria-hidden
            className="icon"
            style={{ width: "1.5rem", height: "1.5rem", objectFit: "contain" }}
          />
          <span>Vitrine</span>
        </Link>
        {/* Botao de recolher: chevron, NUNCA hamburguer (handoff mede
            "‹"/"›" — o hamburguer le como "abrir outro menu", nao como
            recolher o que ja esta aberto). */}
        <button
          type="button"
          className="nav__collapse-btn"
          onClick={onToggle}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expandir menu" : "Recolher menu"}
        >
          {collapsed ? <ChevronRight className="icon" aria-hidden /> : <ChevronLeft className="icon" aria-hidden />}
        </button>
      </div>

      <div className="nav__scroll scroll-silent" ref={scrollRef}>
        {visibleGroups.map((group) => (
          <div key={group.title}>
            {!collapsed ? <div className="nav__group-label">{group.title}</div> : null}
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                className="nav__item"
                // Recolhido, o CSS esconde TODO span que nao seja .icon —
                // isso inclui a pilula de contagem, nao so o rotulo (achado
                // do smoke comparativo 2026-09-22: contador do canal sumia
                // por completo com o menu fechado, sem nenhum jeito de ver
                // o numero). O tooltip e a unica saida nesse estado, entao
                // ele carrega os dois: nome + contagem.
                title={
                  collapsed
                    ? typeof item.badge === "number" && item.badge > 0
                      ? `${item.label} · ${item.badge}`
                      : item.label
                    : undefined
                }
              >
                {item.icon}
                {/* grow+truncate: o rotulo cede espaco pro contador, nunca
                    o contrario. Sem isto a linha toda transbordava e o rail
                    (overflow:hidden) cortava a pilula de contagem no meio
                    (achado 22/09: "19x" em vez de "198"). */}
                <span className="grow truncate">{item.label}</span>
                {typeof item.badge === "number" && item.badge > 0 ? (
                  <span className="chip chip--mono" style={{ flexShrink: 0 }}>
                    {item.badge}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </div>
        ))}
        {visibleGroups.some((g) => g.loading) && !collapsed ? (
          <div className="nav__group-label">Carregando canais…</div>
        ) : null}
      </div>

      <NavScrollIndicator scrollRef={scrollRef} itemCount={itemCount} />

      <div className="nav__footer">
        <ThemeToggle rail />
        {!collapsed ? (
          <div className="nav__foot-user">
            <div className="nav__foot-user__name">{user?.name ?? "—"}</div>
            <div className="nav__foot-user__role">{user?.role_name ?? roleLabel(user?.role)}</div>
          </div>
        ) : null}
        <button
          type="button"
          className="nav__foot-btn nav__foot-btn--solid"
          onClick={() => void logout()}
          aria-label="Sair"
          title="Sair"
        >
          <LogOut className="icon" aria-hidden />
        </button>
      </div>
    </nav>
  );
}
